import { memo, useCallback, useState } from "react";
import { KeyRoundIcon } from "lucide-react";
import {
  actionRequiredCtaLabel,
  buildGitHubActionRequiredRetryText,
  formatGitHubActionRequiredLabel,
  resolveGitHubActionRequiredDetail,
  type GitHubActionRequiredRequest,
} from "../../session-logic";
import { actionRequiredSettingsHref } from "./githubActionRequired";
import { Button } from "../ui/button";
import { cn } from "~/lib/utils";

interface ComposerActionRequiredPanelProps {
  actionRequired: GitHubActionRequiredRequest;
  onRetry: (actionRequired: GitHubActionRequiredRequest) => Promise<void>;
}

/**
 * Composer-anchored "Forge needs you to do something before it can continue"
 * surface (connect GitHub, sign back in, link a repo, …) — the Claude/Codex
 * pattern where the blocker grows into the input instead of floating in the
 * transcript. Shares the calm orange "ask" aesthetic and canonical copy with
 * the command-approval panel; the matching transcript row is suppressed while
 * this is showing so the ask appears once.
 */
export const ComposerActionRequiredPanel = memo(function ComposerActionRequiredPanel({
  actionRequired,
  onRetry,
}: ComposerActionRequiredPanelProps) {
  const [status, setStatus] = useState<"idle" | "retrying" | "queued" | "failed">("idle");
  const [error, setError] = useState<string | null>(null);

  const title = formatGitHubActionRequiredLabel(actionRequired.action);
  const detail = resolveGitHubActionRequiredDetail(actionRequired);
  const settingsHref = actionRequiredSettingsHref(actionRequired.action);
  const ctaLabel = actionRequiredCtaLabel(actionRequired.action);

  const retry = useCallback(async () => {
    setStatus("retrying");
    setError(null);
    try {
      await onRetry(actionRequired);
      setStatus("queued");
    } catch (retryError) {
      setStatus("failed");
      setError(retryError instanceof Error ? retryError.message : "Retry failed.");
    }
  }, [actionRequired, onRetry]);

  const copyRetry = useCallback(() => {
    navigator.clipboard
      ?.writeText(buildGitHubActionRequiredRetryText(actionRequired))
      .catch(() => undefined);
  }, [actionRequired]);

  return (
    <div className="px-4 py-3.5 sm:px-5 sm:py-4">
      <div
        role="status"
        aria-live="polite"
        className={cn(
          "rounded-lg border border-border/60 border-l-2 border-l-primary bg-muted/25 px-3 py-2.5",
        )}
      >
        <div className="flex items-center gap-2">
          <KeyRoundIcon className="size-4 shrink-0 text-primary" aria-hidden />
          <span className="min-w-0 truncate text-sm font-medium text-foreground">{title}</span>
          {actionRequired.toolName ? (
            <span className="ml-auto max-w-[45%] shrink-0 truncate rounded border border-border/50 bg-background/45 px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground/80">
              {actionRequired.toolName}
            </span>
          ) : null}
        </div>
        <p className="mt-2 text-xs leading-4 text-muted-foreground">{detail}</p>
        {status === "queued" ? (
          <p className="mt-1.5 text-[11px] leading-4 text-success-foreground">
            Retry queued — Forge will continue in this thread.
          </p>
        ) : null}
        {status === "failed" && error ? (
          <p className="mt-1.5 text-[11px] leading-4 text-destructive-foreground">{error}</p>
        ) : null}
        <div className="mt-2.5 flex flex-wrap items-center gap-2">
          {settingsHref ? (
            <Button size="xs" render={<a href={settingsHref}>{ctaLabel}</a>} />
          ) : null}
          <Button
            type="button"
            size="xs"
            variant={settingsHref ? "outline" : "default"}
            disabled={status === "retrying" || status === "queued"}
            onClick={retry}
          >
            {status === "retrying" ? "Retrying…" : status === "queued" ? "Queued" : "Retry now"}
          </Button>
          <Button type="button" size="xs" variant="ghost" onClick={copyRetry}>
            Copy retry
          </Button>
        </div>
      </div>
    </div>
  );
});
